import type { DocMeta } from '../core/documents';
import type { Rect, Size, Zone } from '../core/geometry';
import type { FileRef } from './files';

/**
 * Session storage: MyTXT's own data, kept apart from the user's files.
 *
 * Each open document is one record (metadata, current text, caret, and a file
 * reference when the platform allows one), so a long document never forces a
 * rewrite of the whole session. The session record holds the windows — their
 * tab order, active document and place on the canvas — and which window has
 * focus. Nothing here is ever written into a `.txt` or `.md` file.
 */

export interface StoredDocument {
  meta: DocMeta;
  text: string;
  selection?: { anchor: number; head: number };
  ref?: FileRef;
}

export interface StoredWindow {
  id: string;
  docIds: string[];
  activeId: string | null;
  rect: Rect;
  zone: Zone | null;
  restore: Rect | null;
  z: number;
  workspaceId?: string | null;
  workspaceAccount?: string | null;
  name?: string;
}

export interface StoredSession {
  version: 2;
  windows: StoredWindow[];
  focusedId: string;
  /** The canvas size the geometry was recorded on, to adapt it to another screen. */
  canvas: Size;
}

/** The V1 record: one implicit window. */
export interface LegacySession {
  order: string[];
  activeId: string | null;
}

export interface SessionStorage {
  /** False when the browser refuses local storage (e.g. some private modes). */
  readonly durable: boolean;
  load(): Promise<{ session: StoredSession | LegacySession; documents: StoredDocument[] } | null>;
  putSession(session: StoredSession): Promise<void>;
  putDocument(doc: StoredDocument): Promise<void>;
  deleteDocument(id: string): Promise<void>;
  /** Erase every document record and the session (MyTXT's own data only). */
  clear(): Promise<void>;
}

const DB_NAME = 'mytxt';
const DB_VERSION = 1;
const DOCS = 'documents';
const META = 'session';
const SESSION_KEY = 'current';

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function done(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onabort = tx.onerror = () => reject(tx.error ?? new Error('Transaction failed'));
  });
}

function openDatabase(factory: IDBFactory): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = factory.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(DOCS)) db.createObjectStore(DOCS, { keyPath: 'meta.id' });
      if (!db.objectStoreNames.contains(META)) db.createObjectStore(META);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error('Storage is blocked by another MyTXT window'));
  });
}

export async function createIndexedDbStorage(factory: IDBFactory | undefined = globalThis.indexedDB): Promise<SessionStorage> {
  if (!factory) return createMemoryStorage();
  let db: IDBDatabase;
  try {
    db = await openDatabase(factory);
  } catch {
    return createMemoryStorage();
  }

  return {
    durable: true,

    async load() {
      const tx = db.transaction([DOCS, META], 'readonly');
      const [session, documents] = await Promise.all([
        request(tx.objectStore(META).get(SESSION_KEY)) as Promise<StoredSession | LegacySession | undefined>,
        request(tx.objectStore(DOCS).getAll()) as Promise<StoredDocument[]>,
      ]);
      if (!session) return null;
      return { session, documents };
    },

    async putSession(session) {
      const tx = db.transaction(META, 'readwrite');
      tx.objectStore(META).put(session, SESSION_KEY);
      await done(tx);
    },

    async putDocument(doc) {
      try {
        const tx = db.transaction(DOCS, 'readwrite');
        tx.objectStore(DOCS).put(doc);
        await done(tx);
      } catch (error) {
        // A file reference this browser cannot store must never cost the text itself.
        if (doc.ref === undefined || !(error instanceof DOMException && error.name === 'DataCloneError')) throw error;
        const tx = db.transaction(DOCS, 'readwrite');
        tx.objectStore(DOCS).put({ ...doc, ref: undefined });
        await done(tx);
      }
    },

    async deleteDocument(id) {
      const tx = db.transaction(DOCS, 'readwrite');
      tx.objectStore(DOCS).delete(id);
      await done(tx);
    },

    async clear() {
      const tx = db.transaction([DOCS, META], 'readwrite');
      tx.objectStore(DOCS).clear();
      tx.objectStore(META).clear();
      await done(tx);
    },
  };
}

/** Used when the browser offers no durable storage; work survives only this page. */
export function createMemoryStorage(): SessionStorage {
  let session: StoredSession | LegacySession | null = null;
  const docs = new Map<string, StoredDocument>();
  return {
    durable: false,
    async load() {
      return session ? { session, documents: [...docs.values()] } : null;
    },
    async putSession(s) {
      session = s;
    },
    async putDocument(d) {
      docs.set(d.meta.id, d);
    },
    async deleteDocument(id) {
      docs.delete(id);
    },
    async clear() {
      docs.clear();
      session = null;
    },
  };
}
